import { useEffect, useRef, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {Search, Eye, Loader2, FileDown, FileCheck2, CheckCircle2, XCircle, Clock, Globe, Filter, ChevronDown, Upload, X, Paperclip, FileText, } from 'lucide-react'
import type { Configuracao, Solicitacao } from '@/types'
import { gerarPDF } from '@/utils/gerarPDF'
import { formatarData } from '@/utils/protocolo'
import { STATUS_COLORS } from '@/types'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

const BRAND = '#0f3b50'

type AcaoModal = 'aprovar' | 'reprovar' | 'documentoAssinado'

export default function SolicitacoesOnlinePage() {
  const navigate = useNavigate()
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<string>('pendente')
  const [showFilters, setShowFilters] = useState(false)
  const [pdfLoading, setPdfLoading] = useState<string | null>(null)

  // Modal de ação
  const [modalSol, setModalSol] = useState<Solicitacao | null>(null)
  const [modalAcao, setModalAcao] = useState<AcaoModal | null>(null)
  const [obsModal, setObsModal] = useState('')
  const [salvandoModal, setSalvandoModal] = useState(false)

  // Upload documento assinado
  const [docAssinado, setDocAssinado] = useState<File | null>(null)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const docInputRef = useRef<HTMLInputElement>(null)

  const fetch = () => {
    lumi.entities.solicitacoes
      .list({ sort: { createdAt: -1 }, limit: 500 })
      .then((r) => {
        const todas = (r.list || []) as Solicitacao[]
        // Filtra apenas as que vieram do portal público (origem === 'online')
        setSolicitacoes(todas.filter((s: any) => s.origem === 'online'))
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => { fetch() }, [])

  const filtered = solicitacoes.filter(s => {
    const q = search.toLowerCase()
    const matchSearch = !q || s.protocolo?.toLowerCase().includes(q) ||
      s.nomeCompleto?.toLowerCase().includes(q) || s.cpf?.toLowerCase().includes(q)
    if (!matchSearch) return false
    if (filtroStatus === 'pendente') return !s.finalizado && s.status !== 'Aprovada' && s.status !== 'Reprovada'
    if (filtroStatus === 'aprovada') return s.status === 'Aprovada'
    if (filtroStatus === 'reprovada') return s.status === 'Reprovada'
    return true
  })

  const pendentes  = solicitacoes.filter((s: any) => !s.finalizado && s.status !== 'Aprovada' && s.status !== 'Reprovada').length
  const aprovadas  = solicitacoes.filter((s: any) => s.status === 'Aprovada').length
  const reprovadas = solicitacoes.filter((s: any) => s.status === 'Reprovada').length

  const handleGerarPDF = async (s: Solicitacao) => {
    setPdfLoading(s._id)
    try {
      let cfg: Configuracao | null = null
      try { const r = await lumi.entities.configuracoes.list({ limit: 1 }); cfg = (r.list?.[0] as Configuracao) || null } catch { /**/ }
      const histRes = await lumi.entities.historico_status.list({ filter: { solicitacaoId: s._id }, sort: { createdAt: -1 }, limit: 50 })
      await gerarPDF(s, (histRes.list || []) as any[], cfg)
      toast.success('PDF gerado!')
    } catch { toast.error('Erro ao gerar PDF.') }
    finally { setPdfLoading(null) }
  }

  const abrirModal = (sol: Solicitacao, acao: AcaoModal) => {
    setModalSol(sol)
    setModalAcao(acao)
    setObsModal('')
    setDocAssinado(null)
  }

  const fecharModal = () => {
    setModalSol(null)
    setModalAcao(null)
    setObsModal('')
    setDocAssinado(null)
  }

  const handleAprovar = async () => {
    if (!modalSol) return
    if (modalAcao === 'documentoAssinado' && !docAssinado) {
      toast.error('Selecione o documento assinado antes de confirmar.')
      return
    }
    setSalvandoModal(true)
    try {
      const now = new Date().toISOString()
      let pdfAssinadoUrl: string | undefined

      if (docAssinado) {
        setUploadingDoc(true)
        const results = await lumi.tools.file.upload([docAssinado])
        pdfAssinadoUrl = (results[0] as any)?.fileUrl
        setUploadingDoc(false)
      }

      const novoStatus = modalAcao === 'reprovar' ? 'Reprovada' : 'Aprovada'

      await lumi.entities.solicitacoes.update(modalSol._id, {
        status: novoStatus,
        ...(modalAcao === 'aprovar' || modalAcao === 'documentoAssinado' ? {
          finalizado: true,
          dataFinalizacao: now,
          ...(pdfAssinadoUrl ? { pdfAssinadoUrl } : {}),
        } : {}),
        updatedAt: now,
      })

      await lumi.entities.historico_status.create({
        solicitacaoId: modalSol._id,
        protocolo: modalSol.protocolo,
        status: novoStatus,
        responsavel: 'Gestão GEOA',
        observacoes: obsModal || (modalAcao === 'reprovar' ? 'Solicitação reprovada' : 'Solicitação aprovada'),
        creator: 'admin',
        createdAt: now,
        updatedAt: now,
      })

      toast.success(novoStatus === 'Aprovada' ? 'Solicitação aprovada com sucesso!' : 'Solicitação reprovada.')
      fecharModal()
      fetch()
    } catch {
      toast.error('Erro ao processar ação.')
    } finally {
      setSalvandoModal(false)
      setUploadingDoc(false)
    }
  }

  const inputCls = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0f3b50] focus:border-transparent transition'

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#e8f1f5' }}>
            <Globe className="w-5 h-5" style={{ color: BRAND }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Solicitações Online</h1>
            <p className="text-sm text-gray-500 mt-0.5">Solicitações recebidas pelo portal público</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setFiltroStatus('pendente')}
          className={`bg-white rounded-3xl border p-4 text-left transition hover:shadow-md ${filtroStatus === 'pendente' ? 'border-[#0f3b50] ring-2 ring-[#0f3b50]/20' : 'border-gray-100'}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" style={{ color: '#d97706' }} />
            <span className="text-xs font-semibold text-gray-500">Pendentes</span>
          </div>
          <p className="text-2xl font-extrabold" style={{ color: '#d97706' }}>{pendentes}</p>
        </button>
        <button
          onClick={() => setFiltroStatus('aprovada')}
          className={`bg-white rounded-3xl border p-4 text-left transition hover:shadow-md ${filtroStatus === 'aprovada' ? 'border-green-600 ring-2 ring-green-200' : 'border-gray-100'}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-xs font-semibold text-gray-500">Aprovadas</span>
          </div>
          <p className="text-2xl font-extrabold text-green-600">{aprovadas}</p>
        </button>
        <button
          onClick={() => setFiltroStatus('reprovada')}
          className={`bg-white rounded-3xl border p-4 text-left transition hover:shadow-md ${filtroStatus === 'reprovada' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-100'}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-semibold text-gray-500">Reprovadas</span>
          </div>
          <p className="text-2xl font-extrabold text-red-500">{reprovadas}</p>
        </button>
      </div>

      {/* Busca */}
      <div className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Protocolo, nome, CPF..."
              className="w-full pl-9 pr-4 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              onFocus={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 2px ${BRAND}20` }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = '' }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 px-3 py-2 border rounded-2xl text-sm transition"
            style={{ borderColor: '#e5e7eb', color: '#4b5563' }}
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filtro</span>
            <ChevronDown className={`w-3 h-3 transition ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {showFilters && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex gap-2 flex-wrap">
              {[
                { value: '', label: 'Todos' },
                { value: 'pendente', label: 'Pendentes' },
                { value: 'aprovada', label: 'Aprovadas' },
                { value: 'reprovada', label: 'Reprovadas' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setFiltroStatus(opt.value)}
                  className={`px-4 py-1.5 rounded-2xl text-xs font-semibold border transition ${filtroStatus === opt.value ? 'border-[#0f3b50] bg-[#e8f1f5] text-[#0f3b50]' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14">
            <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: BRAND }} />
            <span className="text-sm text-gray-500">Carregando...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Globe className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Nenhuma solicitação online encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Protocolo</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3 hidden md:table-cell">Solicitante</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3 hidden lg:table-cell">Telefone</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3 hidden sm:table-cell">Data</th>
                  <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
                  <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => {
                  const cls = STATUS_COLORS[s.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                  const isPendente = !s.finalizado && s.status !== 'Aprovada' && s.status !== 'Reprovada'
                  return (
                    <tr key={s._id} className={`${i > 0 ? 'border-t border-gray-50' : ''} hover:bg-gray-50/50 transition`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Globe className="w-3 h-3 text-blue-400 flex-shrink-0" />
                          <span className="text-xs font-mono font-bold text-gray-900">{s.protocolo}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div>
                          <p className="text-sm text-gray-800 font-medium">{s.nomeCompleto}</p>
                          <p className="text-xs text-gray-400">{s.cpf}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-sm text-gray-600">{s.telefone}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-sm text-gray-500">{formatarData(s.createdAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium whitespace-nowrap ${cls}`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Visualizar */}
                          <button onClick={() => navigate(`/solicitacoes/${s._id}`)}
                            className="p-1.5 text-gray-400 hover:bg-blue-50 rounded-xl transition"
                            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = BRAND}
                            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = ''}
                            title="Visualizar"><Eye className="w-3.5 h-3.5" /></button>

                          {/* Baixar PDF */}
                          <button onClick={() => handleGerarPDF(s)} disabled={pdfLoading === s._id}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition disabled:opacity-50"
                            title="Baixar PDF para assinatura">
                            {pdfLoading === s._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                          </button>

                          {/* Aprovar (abre modal para anexar documento assinado) */}
                          {isPendente && (
                            <button onClick={() => abrirModal(s, 'aprovar')}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition"
                              title="Aprovar">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {/* Reprovar */}
                          {isPendente && (
                            <button onClick={() => abrirModal(s, 'reprovar')}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
                              title="Reprovar">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {/* PDF Assinado disponível */}
                          {s.status === 'Aprovada' && s.pdfAssinadoUrl && (
                            <a href={s.pdfAssinadoUrl} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-xl transition"
                              title="Ver PDF Assinado">
                              <FileCheck2 className="w-3.5 h-3.5" />
                            </a>
                          )}

                          {/* Anexar documento assinado (aprovadas sem PDF) */}
                          {s.status === 'Aprovada' && !s.pdfAssinadoUrl && (
                            <button onClick={() => abrirModal(s, 'documentoAssinado')}
                              className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded-xl transition"
                              title="Anexar documento assinado">
                              <Paperclip className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Ação */}
      {modalSol && modalAcao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md p-6 space-y-5">

            {/* Cabeçalho do modal */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {modalAcao === 'aprovar' && 'Aprovar Solicitação'}
                  {modalAcao === 'reprovar' && 'Reprovar Solicitação'}
                  {modalAcao === 'documentoAssinado' && 'Anexar Documento Assinado'}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5 font-mono">{modalSol.protocolo}</p>
              </div>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Info do solicitante */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-1">
              <p className="text-sm font-semibold text-gray-800">{modalSol.nomeCompleto}</p>
              <p className="text-xs text-gray-500">CPF: {modalSol.cpf}</p>
              <p className="text-xs text-gray-500">Telefone: {modalSol.telefone}</p>
              <p className="text-xs text-gray-500">Árvore: {modalSol.tipoArvore} — {modalSol.bairroNome}</p>
            </div>

            {/* Reprovar: aviso vermelho */}
            {modalAcao === 'reprovar' && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  Ao reprovar, a solicitação será marcada como <strong>Reprovada</strong> e o cidadão deverá ser notificado pelo telefone <strong>{modalSol.telefone}</strong>.
                </p>
              </div>
            )}

            {/* Aprovar: upload de PDF assinado */}
            {(modalAcao === 'aprovar' || modalAcao === 'documentoAssinado') && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
                    {modalAcao === 'documentoAssinado' ? 'Documento Assinado *' : 'Documento Assinado (opcional)'}
                  </p>
                  <p className="text-xs text-gray-400 mb-3">
                    {modalAcao === 'documentoAssinado'
                      ? 'Faça upload do documento PDF assinado para disponibilizá-lo.'
                      : 'Faça upload do PDF assinado após levar para assinatura. Pode ser feito agora ou depois.'}
                  </p>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={e => setDocAssinado(e.target.files?.[0] || null)}
                  />
                  {!docAssinado ? (
                    <button
                      type="button"
                      onClick={() => docInputRef.current?.click()}
                      className="flex flex-col items-center justify-center gap-2 w-full py-5 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-[#0f3b50] hover:bg-[#e8f1f5] hover:text-[#0f3b50] transition"
                    >
                      <Upload className="w-5 h-5" />
                      <span className="text-sm font-medium">Clique para selecionar o documento</span>
                      <span className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-2xl">
                      <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-green-800 truncate">{docAssinado.name}</p>
                        <p className="text-xs text-green-600">{(docAssinado.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDocAssinado(null)}
                        className="text-gray-400 hover:text-red-500 transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Observação */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Observação {modalAcao === 'reprovar' ? '*' : '(opcional)'}
              </label>
              <textarea
                value={obsModal}
                onChange={e => setObsModal(e.target.value)}
                placeholder={
                  modalAcao === 'reprovar'
                    ? 'Informe o motivo da reprovação...'
                    : 'Observações sobre a aprovação...'
                }
                rows={3}
                className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0f3b50]"
              />
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-1">
              <button
                onClick={fecharModal}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleAprovar}
                disabled={salvandoModal || uploadingDoc || (modalAcao === 'reprovar' && !obsModal.trim())}
                className="flex-1 py-2.5 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: modalAcao === 'reprovar' ? '#dc2626' : '#16a34a' }}
              >
                {(salvandoModal || uploadingDoc) ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processando...</>
                ) : (
                  <>
                    {modalAcao === 'reprovar' ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                    {modalAcao === 'reprovar' ? 'Confirmar Reprovação' : modalAcao === 'documentoAssinado' ? 'Salvar Documento' : 'Confirmar Aprovação'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
